import type { CompatibilityProfile, DeploymentProfile, EditorState, Npc, StaffRole } from '../types/domain.ts'
import { uid } from './ids.ts'
import { sanitizeScriptId } from './identity.ts'

export const DEFAULT_DEPENDENCIES: Record<string, boolean> = {
  Denizen: true, Citizens: true, Depenizen: true, Sentinel: true,
  EssentialsX: true, Vault: true, MythicMobs: true, MythicDungeons: true,
  Nexo: true, ValhallaMMO: true, LuckPerms: true, PlaceholderAPI: true,
  GSit: true, WorldGuard: false,
}

export const LUMINOUS_COMPATIBILITY_PROFILE: CompatibilityProfile = {
  id: 'luminous_smp', name: 'Luminous SMP', paperVersion: '26.1.2', readOnly: true,
  plugins: [
    ['Citizens','2.0.43-SNAPSHOT (build 4228)',true], ['Denizen','1.3.3-SNAPSHOT (build 7299-DEV)',true],
    ['Depenizen','2.1.1 (build 885)',true], ['Sentinel','2.9.4-SNAPSHOT (build 534)',true],
    ['MythicMobs','5.13.0-aad3f1c9',true], ['MythicDungeons','2.0.1-SNAPSHOT',true], ['Nexo','1.26',true],
    ['ValhallaMMO','premium_1.12.2',true], ['PlaceholderAPI','2.12.3',true], ['Essentials','2.22.0',true],
    ['Vault','2.20.2',true], ['LuckPerms','5.5.65',true], ['GSit','3.5.1',true],
    ['WorldGuard','Not installed',false,'Region objectives require a trusted manual Denizen event hook.'],
  ].map(([name,version,installed,notes])=>({name:String(name),version:String(version),installed:Boolean(installed),...(notes?{notes:String(notes)}:{})})),
}

export function createDefaultDeploymentProfile(): DeploymentProfile {
  return {
    mode: 'behavior_only', assignmentMode: 'add', entityType: 'player', nameplateMode: 'true', skinName: '', skinAutoUpdate: false,
    lookcloseRange: 6, lookcloseRealistic: false, spawnLocationId: '',
    managed: { name:false, entityType:false, nameplate:false, skin:false, lookclose:false, protected:false, invulnerable:false, collision:false, gravity:false, glowing:false, spawn:false, sentinel:false },
    equipmentManaged: { main:false, off:false, head:false, chest:false, legs:false, boots:false },
    ownership: 'unmanaged', externalAssignments: [],
  }
}

export function createDefaultRuntimeProfile(): EditorState['runtime'] {
  return {
    character: { personality: '', voice: '', tone: '', canonicalLore: '', knowledgeBoundaries: '', secrets: '', fallbackDialogue: 'I have nothing more to add right now.' },
    worldBinding: { settlementId: '', areaId: '', responsibility: '', resourceKeys: [], rumorKeys: [], threatReaction: 'none' },
    life: { schedules: [], ambientEnabled: false, ambientRadius: 8, ambientTurnLimit: 4, ambientCooldownSeconds: 600, sleepWhenDistant: true, combatPersonality: 'none', services: [] },
    relationships: { trust: 0, fear: 0, respect: 0, affinity: 0, greetingThresholds: '', priceMultiplier: 1, questMinimumTrust: 0 },
    movement: { homeLocationId: '', workLocationId: '', patrolPathId: '', speed: 1, arrivalRadius: 1.5, stuckRetries: 2, safeTeleportDistance: 16, companionEnabled: false, formation: 'follow', customFormation: '' },
    presentation: { dialogueMode: 'chat', indicatorMode: 'text_display', greeting: '', authoredSounds: [], gestures: [], accessibilitySummary: true, baseLineDelayTicks:32, charactersPerExtraTick:12, maximumLineDelayTicks:80, menuPageSize:5 },
    cinematics: [], worldFacts: [], hooks: [],
  }
}

export function createBlankEditorState(): EditorState {
  return {
    schemaVersion: 8,
    graph: { blocks: [], edges: [], camera: { x: 420, y: 260, zoom: 1 }, selectedFlow: 'main' },
    assets: { locations: [], shops: [], quests: [], dungeons: [], variables: [], flows: [{ id: 'main', name: 'Main', description: 'Primary NPC logic' }], snippets: [] },
    dependencies: { ...DEFAULT_DEPENDENCIES },
    traits: { lookclose: true, invulnerable: true, collision: true, gravity: true, nameplate: true, glowing: false, protected: true },
    equipment: { main: '', off: '', head: '', chest: '', legs: '', boots: '' },
    sentinel: { enabled: false, health: 20, damage: 2, chase: 20, targets: '', ignores: 'players', guard: '', custom: '' },
    deployment: createDefaultDeploymentProfile(),
    compiler: { mode: 'readable', importedDsc: '' },
    compatibility: structuredClone(LUMINOUS_COMPATIBILITY_PROFILE),
    runtime: createDefaultRuntimeProfile(),
  }
}

export function provisionalNpc(name: string, workspaceId: string, seasonId: string, locationId: string | null, userId: string): Npc {
  const now = new Date().toISOString()
  return {
    id: uid('npc'), workspace_id: workspaceId, season_id: seasonId, location_id: locationId,
    npc_code: 'pending', npc_sequence: 0, name, script_id: sanitizeScriptId(name), citizens_id: null,
    role: '', status: 'draft', notes: '', editor_state: createBlankEditorState(), compiled_dsc: '', revision: 1,
    created_by: userId, updated_by: userId, original_creator_id: userId, original_season_id: seasonId,
    created_at: now, updated_at: now,
  }
}

export function roleLabel(role: StaffRole): string { return role[0].toUpperCase() + role.slice(1) }
